// src/contexts/DashboardContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";
import useDashboardApi from "../utils/dashboardApi";
import { getTierSlotCount, SYSTEM_LIMIT } from "../utils/constants";

const DashboardContext = createContext();

export const useDashboard = () => useContext(DashboardContext);

export const DashboardProvider = ({ children }) => {
  const [dashboardDivesiteIds, setDashboardDivesiteIds] = useState([]);
  const [dashboardDivesites, setDashboardDivesites] = useState([]);
  const [availableSlots, setAvailableSlots] = useState(0);
  const [likedDivesites, setLikedDivesites] = useState([]);
  const [loadingLiked, setLoadingLiked] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { currentUser, userObjectId } = useAuth();
  const { getDashboard, request, likesRequest } = useDashboardApi();

  const fetchDashboardData = useCallback(async () => {
    if (!currentUser || !userObjectId) {
      return;
    }

    try {
      const dashboard = await getDashboard(userObjectId);
      if (dashboard && dashboard.slots) {
        const initialDashboardDivesiteIds = dashboard.slots
          .filter((slot) => slot && slot._id)
          .map((slot) => slot._id)
          .filter(Boolean);

        setDashboardDivesiteIds(initialDashboardDivesiteIds);
        setDashboardDivesites(dashboard.slots.filter(Boolean));

        const totalSlots = dashboard.maxSlots || SYSTEM_LIMIT;
        setAvailableSlots(totalSlots - initialDashboardDivesiteIds.length);
      } else {
        setDashboardDivesiteIds([]);
        setDashboardDivesites([]);
        setAvailableSlots(SYSTEM_LIMIT);
      }
    } catch (err) {
      console.error("Error fetching dashboard:", err);
      setDashboardDivesiteIds([]);
      setDashboardDivesites([]);
      setAvailableSlots(SYSTEM_LIMIT);
    }
  }, [currentUser, userObjectId, getDashboard]);

  const fetchLikedDivesites = useCallback(async () => {
    setLoadingLiked(true);
    try {
      const fetchedResponse = await likesRequest(
        `diver/${userObjectId}/likedDivesites?page=1&limit=10`
      );
      if (fetchedResponse?.success && Array.isArray(fetchedResponse.data)) {
        setLikedDivesites(fetchedResponse.data);
      } else {
        setLikedDivesites([]);
      }
    } catch (err) {
      console.error("Error fetching liked divesites:", err);
      setLikedDivesites([]);
    } finally {
      setLoadingLiked(false);
    }
  }, [userObjectId, likesRequest]);

  useEffect(() => {
    if (currentUser && userObjectId) {
      fetchDashboardData();
      fetchLikedDivesites();
    }
  }, [currentUser, userObjectId, fetchDashboardData, fetchLikedDivesites]);

  const refreshDashboard = useCallback(() => {
    fetchDashboardData();
    fetchLikedDivesites();
  }, [fetchDashboardData, fetchLikedDivesites]);

  const value = {
    dashboardDivesiteIds,
    dashboardDivesites,
    availableSlots,
    likedDivesites,
    loadingLiked,
    isDeleting,
    setIsDeleting,
    refreshDashboard,
    getTierSlotCount,
    fetchLikedDivesites, // Expose for specific refreshes
    fetchDashboardData, // Expose for specific refreshes
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};
